import { useState } from "react";
import { newVaultKey, wrapVaultKey } from "../crypto";
import { api, errMsg } from "../lib/api";
import { PERM_LABEL } from "../lib/itemTypes";
import { Button, Field, Input } from "../ui/ds";
import { useToast, type SessionInfo } from "../state/store";
import { BackupCodesPanel } from "./Enrol";
import { TotpEnrol } from "./Enrol";
import { GateCard } from "./GateCard";
import { openSession, passwordProblem, prepareAccount, rememberEmail, type PreparedAccount } from "./session";

type Mode =
  | { kind: "setup" }
  | { kind: "invite"; token: string; email: string; name: string; vaultName: string; perm: "view" | "edit" | "add" };

/**
 * Account creation, shared by the owner's first-run set-up and a client
 * accepting an invite: details → authenticator → backup codes. Keys, the TOTP
 * secret and backup codes are all generated here; the server gets the
 * protected blobs, a hash-able auth key and the codes to hash.
 */
export function Onboard({ mode, onDone }: { mode: Mode; onDone: (s: SessionInfo) => void }) {
  const toast = useToast();
  const isInvite = mode.kind === "invite";
  const [step, setStep] = useState<"details" | "totp" | "codes">("details");
  const [name, setName] = useState(isInvite ? mode.name : "");
  const [email, setEmail] = useState(isInvite ? mode.email : "");
  const [setupToken, setSetupToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [prepared, setPrepared] = useState<PreparedAccount | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);

  const submitDetails = async () => {
    if (!name.trim()) return setErr("Enter your name.");
    if (!isInvite && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setErr("Enter a valid email address.");
    if (!isInvite && !setupToken.trim()) return setErr("Enter the set-up code.");
    const problem = passwordProblem(pw, pw2);
    if (problem) return setErr(problem);
    setBusy(true);
    setErr("");
    try {
      setPrepared(await prepareAccount(name.trim(), pw));
      setPw("");
      setPw2("");
      setStep("totp");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submitTotp = async (code: string) => {
    if (!prepared) return;
    if (code.replace(/\D/g, "").length !== 6) return setErr("The code is 6 digits. Check your authenticator app.");
    setBusy(true);
    setErr("");
    try {
      if (mode.kind === "setup") {
        const { key } = await newVaultKey();
        await api("/setup", {
          body: {
            ...prepared.payload,
            totpCode: code,
            setupToken: setupToken.trim(),
            email: email.trim().toLowerCase(),
            personalVaultKey: await wrapVaultKey(key, prepared.keys.publicKeyB64),
          },
        });
      } else {
        await api(`/invites/${encodeURIComponent(mode.token)}/accept`, { body: { ...prepared.payload, totpCode: code } });
      }
      rememberEmail(isInvite ? mode.email : email.trim().toLowerCase());
      setSession(await openSession(prepared.keys));
      setStep("codes");
    } catch (e) {
      const msg = errMsg(e);
      setErr(msg);
      // A wrong set-up code is fixed on the first step.
      if (/set-up code/i.test(msg)) setStep("details");
    } finally {
      setBusy(false);
    }
  };

  const finish = async (saved: boolean) => {
    if (!session) return;
    if (saved) {
      try {
        await api("/me/backup-codes/saved", { method: "POST" });
        session.backup.saved = true;
      } catch (e) {
        toast("Couldn't record that", errMsg(e), "warning");
      }
    }
    if (isInvite) window.history.replaceState(null, "", "/");
    onDone(session);
  };

  const eyebrow = isInvite ? "Shared by Agile Software" : "Set up Agile Vault";
  const accountEmail = isInvite ? mode.email : email.trim().toLowerCase();

  if (step === "details") {
    return (
      <GateCard
        eyebrow={eyebrow}
        title={isInvite ? `Join ${mode.vaultName}` : "Create your vault"}
        sub={
          isInvite
            ? `Agile Software is sharing credentials with you here (${PERM_LABEL[mode.perm].toLowerCase()}). Choose a master password. Nobody else, including us, can recover it.`
            : "You’re the owner. Choose a master password: it encrypts everything on this device and can’t be recovered if you forget it."
        }
        steps={[1, 3]}
      >
        <form
          className="gate-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void submitDetails();
          }}
        >
          {!isInvite ? (
            <Field label="Set-up code" hint="From the SETUP_TOKEN secret. Only needed once.">
              <Input value={setupToken} mono autoComplete="off" onChange={(v) => { setSetupToken(v); setErr(""); }} />
            </Field>
          ) : null}
          <Field label="Your name">
            <Input value={name} autoComplete="name" onChange={(v) => { setName(v); setErr(""); }} />
          </Field>
          {isInvite ? (
            <div className="email-chip">
              <span>{mode.email}</span>
            </div>
          ) : (
            <Field label="Email">
              <Input type="email" icon="mail" autoComplete="username" value={email} onChange={(v) => { setEmail(v); setErr(""); }} />
            </Field>
          )}
          <Field label="Master password" hint="At least 12 characters. A few random words works well.">
            <Input type="password" autoComplete="new-password" value={pw} onChange={(v) => { setPw(v); setErr(""); }} />
          </Field>
          <Field label="Confirm master password">
            <Input type="password" autoComplete="new-password" value={pw2} onChange={(v) => { setPw2(v); setErr(""); }} />
          </Field>
          <Button type="submit" fullWidth arrow busy={busy}>
            {busy ? "Generating your keys…" : "Continue"}
          </Button>
          {err ? <div className="gate-error" role="alert">{err}</div> : null}
        </form>
      </GateCard>
    );
  }

  if (step === "totp" && prepared) {
    return (
      <GateCard eyebrow={eyebrow} title="Set up two-step verification" sub="Everyone signs in with an authenticator code as well as their password." steps={[2, 3]}>
        <TotpEnrol secret={prepared.totpSecret} account={accountEmail} busy={busy} error={err} onSubmit={(c) => void submitTotp(c)} />
      </GateCard>
    );
  }

  return (
    <GateCard
      eyebrow={eyebrow}
      title="Save your backup codes"
      sub="If you lose your phone, each of these gets you in once. Keep them somewhere other than this vault."
      steps={[3, 3]}
      wide
    >
      <BackupCodesPanel codes={prepared?.backupCodes ?? []} email={accountEmail} />
      <div className="gate-stack" style={{ marginTop: 16 }}>
        <Button fullWidth arrow onClick={() => void finish(true)}>
          I’ve saved them
        </Button>
        {isInvite ? (
          <button type="button" className="linklike is-muted" onClick={() => void finish(false)}>
            I’ll do it later (you won’t get access until you do)
          </button>
        ) : (
          <button type="button" className="linklike is-muted" onClick={() => void finish(false)}>
            Remind me later
          </button>
        )}
      </div>
    </GateCard>
  );
}
